import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { logger } from '@deskbay/shared';
import { storage } from '../services/storage';
import { platform } from '../utils/platform';
import { useAuth } from './AuthContext';
import { recordMultipleConsents, getOrCreateSessionId } from '../services/consentService';

/**
 * Categories of cookies/scripts
 */
export interface CookieConsent {
    essential: boolean; // Always true
    analytics: boolean; // GTM, Clarity, GA4
    marketing: boolean; // Google Ads (future use)
}

interface CookieContextType {
    consent: CookieConsent;
    updateConsent: (category: keyof CookieConsent, value: boolean) => void;
    acceptAll: () => void;
    rejectAll: () => void;
    savePreferences: (preferences: Partial<CookieConsent>) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    hasInteracted: boolean;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const COOKIE_STORAGE_KEY = 'deskbay_cookie_consent';

const DEFAULT_CONSENT: CookieConsent = {
    essential: true,
    analytics: false, // GDPR Opt-in: disabled by default
    marketing: false, // GDPR Opt-in: disabled by default
};

const ACCEPTED_ALL_CONSENT: CookieConsent = {
    essential: true,
    analytics: true,
    marketing: true,
};

const REJECTED_CONSENT: CookieConsent = {
    essential: true,
    analytics: false,
    marketing: false,
};

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { session } = useAuth();
    const [consent, setConsent] = useState<CookieConsent>(DEFAULT_CONSENT);
    const [isOpen, setIsOpen] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [hasInteracted, setHasInteracted] = useState(false);

    useEffect(() => {
        const loadConsent = async () => {
            // GATE 1: Native apps (iOS/Android) never show cookie banner.
            // Cookies/GTM/Clarity are web-only. Consent is handled in onboarding.
            // Also suppressed when opened from native via in-app browser (?native=true).
            if (platform.isNative || new URLSearchParams(window.location.search).get('native') === 'true') {
                setIsInitialized(true);
                return;
            }

            const stored = await storage.get(COOKIE_STORAGE_KEY);
            if (stored) {
                try {
                    setConsent(JSON.parse(stored));
                    setHasInteracted(true);
                } catch (e) {
                    logger.error.error('Failed to parse cookie consent', e, 'CookieContext');
                    // GATE 2: Authenticated users accepted in onboarding, no banner needed
                    if (!session) {
                        setIsOpen(true);
                    }
                }
            } else {
                // First visit: GDPR opt-in — show banner ONLY for anonymous web users
                if (!session) {
                    setIsOpen(true);
                }
            }
            setIsInitialized(true);
        };
        loadConsent();
    }, [session]);

    const saveConsent = useCallback((newConsent: CookieConsent) => {
        setConsent(newConsent);
        setHasInteracted(true);
        storage.set(COOKIE_STORAGE_KEY, JSON.stringify(newConsent));

        // Write to DB (fire-and-forget) — GDPR audit trail
        const userId = session?.user?.id;
        const sessionId = userId ? undefined : getOrCreateSessionId();

        recordMultipleConsents([
            {
                userId,
                sessionId,
                consentType: 'cookie_essential',
                consentGiven: true,
            },
            {
                userId,
                sessionId,
                consentType: 'cookie_analytics',
                consentGiven: newConsent.analytics,
            },
            {
                userId,
                sessionId,
                consentType: 'cookie_marketing',
                consentGiven: newConsent.marketing,
            },
        ]);
    }, [session]);

    const updateConsent = useCallback((category: keyof CookieConsent, value: boolean) => {
        if (category === 'essential') return; // Cannot change essential
        saveConsent({ ...consent, [category]: value });
    }, [consent, saveConsent]);

    const acceptAll = useCallback(() => {
        saveConsent(ACCEPTED_ALL_CONSENT);
        setIsOpen(false);
    }, [saveConsent]);

    const rejectAll = useCallback(() => {
        saveConsent(REJECTED_CONSENT);
        setIsOpen(false);
    }, [saveConsent]);

    const savePreferences = useCallback((preferences: Partial<CookieConsent>) => {
        saveConsent({ ...consent, ...preferences, essential: true });
        setIsOpen(false);
    }, [consent, saveConsent]);

    if (!isInitialized) {
        return null;
    }

    return (
        <CookieContext.Provider value={{ consent, updateConsent, acceptAll, rejectAll, savePreferences, isOpen, setIsOpen, hasInteracted }}>
            {children}
        </CookieContext.Provider>
    );
};

export const useCookieConsent = () => {
    const context = useContext(CookieContext);
    if (!context) {
        throw new Error('useCookieConsent must be used within a CookieProvider');
    }
    return context;
};
